/**
 * ENG-436 — one input box, two interfaces.
 *
 * Search and Luma chat accept the same input. This module decides which one
 * should service a given query, and (for searches) hands back a refined
 * keyword query so the user never has to think about phrasing.
 *
 * The decision runs in three tiers, cheapest first:
 *
 *   1. `force` — the user held a modifier key. Instant, no network round.
 *   2. Heuristics — cheap, local, and decisive only for inputs whose shape
 *      leaves no real doubt (see `QUERY_HEURISTICS` in `utils/query-routing`).
 *   3. The LLM — everything else, via `classifyQuery`. Also returns the
 *      refined search query.
 *
 * Tiers 1 and 2 deliberately skip refinement: every input they resolve is
 * already either terse (an identifier, a two-word phrase) or explicitly
 * chosen by the user, so there is nothing worth a round trip to rewrite.
 */

import type { QueryClassification } from '@/services/llm-service';
import type { QueryHeuristicsConfig, QueryMode, QueryRoute } from '@/utils/query-routing';
import { ref } from 'vue';
import { classifyQuery } from '@/services/llm-service';
import { heuristicRoute } from '@/utils/query-routing';

export type {
   HeuristicVerdict,
   QueryHeuristicsConfig,
   QueryMode,
   QueryRoute,
   QueryRouteSource,
} from '@/utils/query-routing';
export { forcedModeFromEvent, heuristicRoute, QUERY_HEURISTICS } from '@/utils/query-routing';

/* ---------------------------------------------------------------------------------------------- */

export interface ResolveQueryRouteOptions {
   /** Skip the analysis entirely — the user asked for this interface by name. */
   force?: QueryMode | null;
   /** Heuristic tuning for this call. */
   heuristics?: Partial<QueryHeuristicsConfig>;
   /** Seam for tests. */
   classify?: (question: string) => Promise<QueryClassification | null>;
}

/**
 * Decide which interface should service `input`.
 *
 * Never rejects: an unavailable or malformed classifier degrades to search,
 * which is the safer default for the inputs that reach that tier (statement
 * shaped, no question marker) and is one click away from chat either way.
 */
export async function resolveQueryRoute(
   input: string,
   options: ResolveQueryRouteOptions = {},
): Promise<QueryRoute> {
   const { force = null, heuristics, classify = classifyQuery } = options;
   const trimmed = input.trim();

   if (!trimmed)
      return { mode: 'search', searchQuery: '', message: '', source: 'fallback', rule: 'empty' };

   if (force) {
      return {
         mode: force,
         searchQuery: trimmed,
         message: trimmed,
         source: 'override',
         rule: 'user-override',
      };
   }

   const verdict = heuristicRoute(trimmed, heuristics);
   if (verdict) {
      return {
         mode: verdict.mode,
         searchQuery: trimmed,
         message: trimmed,
         source: 'heuristic',
         rule: verdict.rule,
      };
   }

   const classification = await classify(trimmed);
   if (classification) {
      return {
         mode: classification.type,
         searchQuery: classification.search_query,
         message: trimmed,
         source: 'llm',
         rule: 'classifier',
      };
   }

   return {
      mode: 'search',
      searchQuery: trimmed,
      message: trimmed,
      source: 'fallback',
      rule: 'classifier-unavailable',
   };
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * Component-facing wrapper that tracks whether an analysis round is in flight,
 * so the submit control can show a spinner instead of appearing to hang.
 */
export function useQueryRouter() {
   const isResolving = ref(false);

   async function resolve(
      input: string,
      options: ResolveQueryRouteOptions = {},
   ): Promise<QueryRoute> {
      isResolving.value = true;
      try {
         return await resolveQueryRoute(input, options);
      }
      finally {
         isResolving.value = false;
      }
   }

   return { isResolving, resolve };
}
